/**
 * 模块: Keyword Basic Columns
 * 职责: 关键词表格基础列定义
 * 限制: <280行
 */

import React from 'react';
import { Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { renderRank, rankSorter, renderGrowthRate, metricSorter, getPercentClass } from './columnUtils';

/**
 * 关键词基础列（关键词、排名、周期覆盖、ABA得分等）
 */
export const basicColumns: ColumnType<any>[] = [
  {
    title: '关键词',
    dataIndex: 'keyword',
    key: 'keyword',
    fixed: 'left',
    width: 180,
    ellipsis: true,
    // 添加筛选搜索功能
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <input
          placeholder="搜索关键词"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              confirm();
            }
          }}
          style={{
            width: 188,
            marginBottom: 8,
            display: 'block',
            padding: '4px 11px',
            border: '1px solid #d9d9d9',
            borderRadius: '2px',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={() => confirm()}
            style={{
              padding: '4px 15px',
              border: '1px solid #1890ff',
              background: '#1890ff',
              color: 'white',
              borderRadius: '2px',
              cursor: 'pointer',
            }}
          >
            搜索
          </button>
          <button
            type="button"
            onClick={() => {
              clearFilters();
              confirm();
            }}
            style={{
              padding: '4px 15px',
              border: '1px solid #d9d9d9',
              background: 'white',
              borderRadius: '2px',
              cursor: 'pointer',
            }}
          >
            重置
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
    ),
    onFilter: (value, record: any) =>
      record.keyword?.toLowerCase().includes(String(value).toLowerCase()),
  },
  {
    title: <Tooltip title="平均搜索频率排名">平均排名</Tooltip>,
    dataIndex: 'search_frequency_rank',
    key: 'search_frequency_rank',
    align: 'center',
    sorter: (a: any, b: any) => rankSorter(a, b, 'search_frequency_rank'),
    defaultSortOrder: 'ascend',
    render: renderRank,
  },
  {
    title: <Tooltip title="最新周期的搜索频率排名">最新排名</Tooltip>,
    dataIndex: 'latest_search_frequency_rank',
    key: 'latest_search_frequency_rank',
    align: 'center',
    sorter: (a: any, b: any) => rankSorter(a, b, 'latest_search_frequency_rank'),
    render: renderRank,
  },
  {
    title: <Tooltip title="排名增长率 - 正值表示排名改善">排名CAGR(%)</Tooltip>,
    dataIndex: 'rank_cagr',
    key: 'rank_cagr',
    align: 'center',
    sorter: (a: any, b: any) => (a.rank_cagr ?? 0) - (b.rank_cagr ?? 0),
    render: renderGrowthRate,
  },
  {
    title: <Tooltip title="出现周期/总周期">周期覆盖</Tooltip>,
    key: 'periods_coverage',
    align: 'center',
    render: (record: any) => `${record.periods_count}/${record.total_periods}`,
    sorter: (a: any, b: any) => (a.periods_count || 0) - (b.periods_count || 0),
  },
  {
    title: <Tooltip title="ABA得分 = 1/平均排名 × 1,000,000">ABA得分</Tooltip>,
    dataIndex: ['metrics', 'rank_score'],
    key: 'rank_score',
    align: 'center',
    render: (value: number) => {
      if (value === null || value === undefined) return '-';
      // ABA得分区间: >10000为优秀, >1000为良好, >100为一般
      const className = getPercentClass(
        value >= 10000 ? 90 : value >= 1000 ? 70 : value >= 100 ? 50 : 30,
        { excellent: 80, good: 60, moderate: 40 }
      );
      return (
        <Tooltip title={`ABA得分: ${value.toLocaleString()}（基于平均排名，排名越靠前得分越高）`}>
          <span className={`metric-value ${className}`}>{value.toLocaleString()}</span>
        </Tooltip>
      );
    },
    sorter: (a: any, b: any) => metricSorter(a, b, 'rank_score'),
  },
  {
    title: <Tooltip title="产品在该关键词的市场控制力">控制度</Tooltip>,
    dataIndex: ['metrics', 'market_control'],
    key: 'market_control',
    align: 'center',
    render: (value: number) => {
      if (value === null || value === undefined) return '-';
      const className = getPercentClass(value, { excellent: 60, good: 40, moderate: 20 });
      return (
        <Tooltip title="越高越好，表示产品在该关键词下的市场控制力">
          <span className={`metric-value ${className}`}>{value.toFixed(1)}%</span>
        </Tooltip>
      );
    },
    sorter: (a: any, b: any) => metricSorter(a, b, 'market_control'),
  },
];

